import React, { useState, useEffect } from "react";
import { ref, set, onValue, remove } from "firebase/database";
import { database } from "../firebase";
import { Pencil, X } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const defaultSteps = [
  { label: "Booking Confirmed", description: "Shipment registered" },
  { label: "Picked Up", description: "Collected by courier agent" },
  { label: "Hyderabad", description: "Reached origin hub" },
  { label: "Mumbai Hub", description: "Sorting completed" },
  { label: "Arrived in USA", description: "Reached destination country" },
  { label: "Dispatched to USA", description: "In transit internationally" },
  { label: "Awating Customs Clearance", description: "Under customs process" },
  { label: "Delivered", description: "Delivered to recipient" }
];

const initialForm = {
  trackingId: "",
  title: "",
  from: "",
  to: "",
  customer: "",
  weight: "",
  invoice: "",
  fromNumber: "",
  toNumber: "",
  serviceTrackingId: "",
  currentStep: 0,
  steps: defaultSteps
};

const normalizeTrackingId = (trackingNumber = "") =>
  trackingNumber.toString().trim().toUpperCase();

const getTrackingURL = (trackingNumber) => {
  const id = normalizeTrackingId(trackingNumber);

  if (!id) return "#";

  if (id.startsWith("1Z")) {
    return `https://www.ups.com/track?tracknum=${encodeURIComponent(id)}`;
  }

  if (id.startsWith("922")) {
    return `https://atlanticcourier.net/track/${encodeURIComponent(id)}`;
  }

  if (id.startsWith("241951")) {
    return `https://www.shreemaruti.com/track-shipment?trackingNumber=${encodeURIComponent(id)}`;
  }

  if (id.startsWith("HNK") || id.startsWith("PRO")) {
    return "https://www.tpcindia.com/track-shipment";
  }

  if (/^\d{12}$/.test(id)) {
    return `https://www.fedex.com/fedextrack/?tracknumbers=${encodeURIComponent(id)}`;
  }

  if (/^\d{10}$/.test(id)) {
    return `https://www.dhl.com/global-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encodeURIComponent(id)}`;
  }

  return `https://www.google.com/search?q=${encodeURIComponent(id + " tracking")}`;
};

const getCarrierName = (trackingNumber = "") => {
  const id = normalizeTrackingId(trackingNumber);

  if (id.startsWith("1Z")) return "UPS";
  if (id.startsWith("922")) return "Atlantic";
  if (id.startsWith("241951")) return "Shree Maruti";
  if (id.startsWith("HNK") || id.startsWith("PRO")) return "TPC India";
  if (/^\d{12}$/.test(id)) return "FedEx";
  if (/^\d{10}$/.test(id)) return "DHL";

  return "Other";
};

const isCompletedEntry = (entry) => {
  const steps = entry.steps || defaultSteps;
  const current = Number(entry.currentStep || 0);

  return current >= steps.length - 1;
};

const AdminPanel = () => {
  const [form, setForm] = useState(initialForm);
  const [entries, setEntries] = useState({});
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const ADMIN_PASSWORD = "courierworld917";


  useEffect(() => {
    return onValue(ref(database), (snapshot) => {
      setEntries(snapshot.val() || {});
    });
  }, []);

  const handleChange = (e, idx = null) => {
    const { name, value } = e.target;

    if (idx !== null) {
      const updatedSteps = [...form.steps];
      updatedSteps[idx][name] = value;
      setForm({ ...form, steps: updatedSteps });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.trackingId.trim()) {
      alert("Tracking ID is required");
      return;
    }

    const payload = {
      ...form,
      currentStep: Number(form.currentStep || 0),
      modified: new Date().toISOString(),
      date: form.date || new Date().toISOString()
    };

    try {
      await set(ref(database, form.trackingId), payload);
      alert("Saved successfully");
      setForm(initialForm);
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleEdit = (id) => {
    setForm({
      ...entries[id],
      trackingId: id
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this entry?")) {
      remove(ref(database, id));
    }
  };

  const getSortValue = ([id, entry]) => {
    switch (sortBy) {
      case "date":
        return entry.date || "";
      case "modified":
        return entry.modified || entry.date || "";
      case "service":
        return getCarrierName(entry.serviceTrackingId);
      case "serviceTrackingId":
        return entry.serviceTrackingId || "";
      case "invoice":
        return entry.invoice || "";
      case "customer":
        return entry.customer || "";
      case "status":
        return isCompletedEntry(entry) ? "Completed" : "On Progress";
      default:
        return entry[sortBy] || id;
    }
  };

  const sortedEntries = Object.entries(entries).sort((a, b) => {
    const A = getSortValue(a);
    const B = getSortValue(b);

    if (!A && !B) return 0;
    if (!A) return 1;
    if (!B) return -1;

    if (sortBy === "date" || sortBy === "modified") {
      const dateA = new Date(A).getTime();
      const dateB = new Date(B).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    }

    if (!isNaN(A) && !isNaN(B)) {
      return sortOrder === "asc"
        ? Number(A) - Number(B)
        : Number(B) - Number(A);
    }

    return sortOrder === "asc"
      ? A.toString().toLowerCase().localeCompare(B.toString().toLowerCase())
      : B.toString().toLowerCase().localeCompare(A.toString().toLowerCase());
  });

  const filteredEntries = sortedEntries.filter(([id, entry]) => {
    const query = search.toLowerCase();

    return [
      id,
      entry.invoice,
      entry.customer,
      entry.fromNumber,
      entry.toNumber,
      entry.to,
      entry.serviceTrackingId,
      getCarrierName(entry.serviceTrackingId),
      isCompletedEntry(entry) ? "Completed" : "On Progress"
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(query));
  });

  const onProgressEntries = filteredEntries.filter(
    ([, entry]) => !isCompletedEntry(entry)
  );

  const completedEntries = filteredEntries.filter(([, entry]) =>
    isCompletedEntry(entry)
  );

  const mapEntryForExport = ([id, e]) => ({
    Invoice: e.invoice || "",
    Customer: e.customer || "",
    FromNumber: e.fromNumber || "",
    ToNumber: e.toNumber || "",
    To: e.to || "",
    Carrier: getCarrierName(e.serviceTrackingId),
    ServiceTrackingId: e.serviceTrackingId || "",
    CW_ID: id,
    CurrentStep: e.currentStep || 0,
    TotalSteps: (e.steps || defaultSteps).length,
    Status: isCompletedEntry(e) ? "Completed" : "On Progress"
  });

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });

    autoTable(doc, {
      head: [
        [
          "Invoice",
          "Customer",
          "From#",
          "To#",
          "To",
          "Carrier",
          "Service ID",
          "CW ID",
          "Status"
        ]
      ],
      body: filteredEntries.map(([id, e]) => [
        e.invoice || "",
        e.customer || "",
        e.fromNumber || "",
        e.toNumber || "",
        e.to || "",
        getCarrierName(e.serviceTrackingId),
        e.serviceTrackingId || "",
        id,
        isCompletedEntry(e) ? "Completed" : "On Progress"
      ])
    });

    doc.save("tracking-data.pdf");
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    const onProgressSheet = XLSX.utils.json_to_sheet(
      onProgressEntries.map(mapEntryForExport)
    );

    const completedSheet = XLSX.utils.json_to_sheet(
      completedEntries.map(mapEntryForExport)
    );

    XLSX.utils.book_append_sheet(wb, onProgressSheet, "On Progress");
    XLSX.utils.book_append_sheet(wb, completedSheet, "Completed");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array"
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });

    saveAs(blob, "tracking-data.xlsx");
  };

   
  if (!isAuthenticated) {
  const handleLogin = (e) => {
    e.preventDefault();

    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      alert("Incorrect Password");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleLogin}
        className="bg-white p-6 rounded-xl shadow max-w-sm w-full border border-gray-200"
      >
        <h2 className="text-xl font-bold text-blue-700 mb-4 text-center">
          Admin Login
        </h2>

        <input
          type="password"
          placeholder="Enter Admin Password"
          value={passwordInput}
          onChange={(e) => setPasswordInput(e.target.value)}
          className="w-full p-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition"
        >
          Login
        </button>
      </form>
    </div>
  );
}



  const renderTable = (tableEntries, emptyText) => (
    <div className="overflow-x-auto">
      <table className="min-w-[1400px] w-full bg-white rounded-xl shadow border">
        <thead className="bg-blue-100 text-blue-800">
          <tr>
            {[
              "Invoice",
              "Customer",
              "From#",
              "To#",
              "To",
              "Service ID",
              "CW ID",
              "Steps",
              "Actions"
            ].map((h, i) => (
              <th key={i} className="p-3 text-center text-sm font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {tableEntries.map(([id, entry]) => (
            <tr key={id} className="border-t hover:bg-blue-50">
              {[
                entry.invoice,
                entry.customer,
                entry.fromNumber,
                entry.toNumber,
                entry.to
              ].map((val, i) => (
                <td key={i} className="p-2 text-center">
                  {val}
                </td>
              ))}

              <td className="text-center">
                <a
                  href={getTrackingURL(entry.serviceTrackingId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline font-medium"
                >
                  {entry.serviceTrackingId}
                </a>

                <div className="text-xs text-gray-500 mt-1">
                  {getCarrierName(entry.serviceTrackingId)}
                </div>
              </td>

              <td className="text-center">{id}</td>

              <td className="p-3">
                <div className="flex justify-center">
                  <div className="flex flex-wrap gap-2 max-w-[320px]">
                    {(entry.steps || defaultSteps).map((step, i) => {
                      const current = Number(entry.currentStep || 0);

                      return (
                        <div key={i} className="flex flex-col items-center">
                          <div
                            title={`${step.label}: ${step.description}`}
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition ${
                              i <= current
                                ? "bg-blue-600 text-white shadow-md"
                                : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            {i < current ? "✓" : i + 1}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </td>

              <td className="text-center space-x-2">
                <button onClick={() => handleEdit(id)}>
                  <Pencil size={16} />
                </button>

                <button onClick={() => handleDelete(id)}>
                  <X size={16} />
                </button>
              </td>
            </tr>
          ))}

          {tableEntries.length === 0 && (
            <tr>
              <td colSpan="9" className="p-6 text-center text-sm text-gray-500">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-2xl font-bold text-blue-700 mb-6">
          Admin Panel
        </h1>

        <div className="bg-white p-6 rounded-xl shadow mb-8">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {[
              ["trackingId", "Tracking ID"],
              ["title", "Title"],
              ["from", "From"],
              ["to", "To"],
              ["customer", "Customer"],
              ["weight", "Weight"],
              ["invoice", "Invoice"],
              ["fromNumber", "From Number"],
              ["toNumber", "To Number"],
              ["serviceTrackingId", "Service Tracking ID"]
            ].map(([name, placeholder]) => (
              <input
                key={name}
                name={name}
                placeholder={placeholder}
                value={form[name]}
                onChange={handleChange}
                className="p-2 rounded-lg border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-blue-400"
              />
            ))}

            <input
              name="currentStep"
              type="number"
              min="0"
              max={form.steps.length - 1}
              placeholder="Current Step"
              value={form.currentStep}
              onChange={handleChange}
              className="p-2 rounded-lg border border-gray-200"
            />

            <button
              type="submit"
              className="md:col-span-2 mt-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition w-fit"
            >
              Save Entry
            </button>
          </form>
        </div>

        <div className="bg-white p-6 rounded-xl shadow mb-8">
          <h2 className="text-lg font-semibold mb-4 text-blue-700">
            Edit Tracking Steps
          </h2>

          {form.steps.map((step, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <input
                name="label"
                placeholder="Step Label"
                value={step.label}
                onChange={(e) => handleChange(e, i)}
                className="p-2 rounded-lg border border-gray-200 bg-white"
              />

              <input
                name="description"
                placeholder="Step Description"
                value={step.description}
                onChange={(e) => handleChange(e, i)}
                className="p-2 rounded-lg border border-gray-200 bg-white"
              />
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              setForm({
                ...form,
                steps: [...form.steps, { label: "", description: "" }]
              })
            }
            className="text-blue-600 underline"
          >
            + Add Step
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-6 bg-white/70 backdrop-blur-md p-4 rounded-xl shadow border border-gray-200">
          <input
            type="text"
            placeholder="Search tracking, customer, invoice..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="p-2 px-3 border border-gray-300 rounded-lg w-[280px] focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 px-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-400"
          >
            <option value="date">Sort by Date</option>
            <option value="modified">Sort by Modified</option>
            <option value="service">Sort by Service</option>
            <option value="serviceTrackingId">Sort by Service Tracking ID</option>
            <option value="invoice">Sort by Invoice</option>
            <option value="customer">Sort by Customer</option>
            <option value="status">Sort by Status</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="p-2 px-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-400"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={exportPDF}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg shadow transition"
            >
              PDF
            </button>

            <button
              onClick={exportExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow transition"
            >
              Excel
            </button>
          </div>
        </div>

        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-blue-700">On Progress</h2>
            <span className="text-sm text-gray-500">
              {onProgressEntries.length} entries
            </span>
          </div>

          {renderTable(onProgressEntries, "No on-progress entries found.")}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-green-700">Completed</h2>
            <span className="text-sm text-gray-500">
              {completedEntries.length} entries
            </span>
          </div>

          {renderTable(completedEntries, "No completed entries found.")}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
